// ============================================================
// TypeScriptAssignment.jsx
// ============================================================
// Junior-dev Notes:
// - Logic: Focuses on Type Safety, Generics, and Structural Typing.
// - UI: Goldenrod accents (#E5C95F) on Neural Purple (#100720).
// - Strategy: 30 Challenges for Enterprise-Grade Type Architecture.
// - Branding: prodevconsult.com | Finnish Engineering Standards
// ============================================================

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    Chip,
} from "@mui/material";

/* Icons */
import { AiFillBulb } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ShieldIcon from "@mui/icons-material/Shield"; // For Type Safety feel
import TerminalIcon from "@mui/icons-material/Terminal";
import ArchitectureIcon from "@mui/icons-material/Architecture";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1516116216624-53e697fedbea";

/* ── Styled Components ───────────────────────────────────── */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.92), rgba(16,7,32,0.96)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.03)",
    borderRadius: "16px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
    border: "1px solid rgba(229, 201, 95, 0.15)",
    color: "white",
    transition: "0.3s ease",
    "&:hover": {
        border: "1px solid rgba(229, 201, 95, 0.4)",
        backgroundColor: "rgba(255,255,255,0.05)",
    }
}));

const QuestionItem = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    "&:last-child": { borderBottom: "none" },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "#E5C95F",
    color: "#100720",
    fontWeight: 800,
    padding: "14px 45px",
    borderRadius: "4px",
    transition: "0.3s",
    "&:hover": {
        backgroundColor: "#FFF0A5",
        transform: "translateY(-3px)",
    },
}));

/* ── Main Component ──────────────────────────────────────── */

export default function TypeScriptAssignment() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleCopy = (questions, index) => {
        const text = questions.join("\n");
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const assignmentData = useMemo(() => [
        {
            title: "Basic Typing & Aliases",
            questions: [
                "Define a 'User' type alias with optional fields and a union type for 'Role'.",
                "Implement a function that accepts 'string | number' and returns the length or value.",
                "Create an Enum for HTTP Status Codes (OK, Created, BadRequest, etc.).",
                "Demonstrate the use of 'Tuple' types for fixed-length coordinates.",
                "Use 'type' vs 'interface' to extend a base 'Product' definition."
            ]
        },
        {
            title: "Interfaces & Contracts",
            questions: [
                "Build an interface for a 'Config' object with readonly properties.",
                "Implement a 'Shape' interface and create classes that 'implements' it.",
                "Define an interface for a function that handles mathematical operations.",
                "Use 'Index Signatures' to create a dynamic dictionary of student grades.",
                "Demonstrate 'Merging Interfaces' and explain why this doesn't work for Type Aliases."
            ]
        },
        {
            title: "Generics Mastery",
            questions: [
                "Write a Generic function 'wrapInArray<T>' that returns an array of any type.",
                "Implement a Generic 'ApiResponse<T>' interface for standardized fetch results.",
                "Create a 'DataStore<T>' class with methods to add and retrieve items by ID.",
                "Use 'Generic Constraints' to ensure a function only accepts objects with a .length property.",
                "Implement a 'keyof' generic function to safely extract properties from an object."
            ]
        },
        {
            title: "Advanced Utility Types",
            questions: [
                "Use 'Partial<T>' to create an update function for a Profile object.",
                "Demonstrate 'Pick<T>' and 'Omit<T>' to create sub-types of a large 'Employee' interface.",
                "Use 'Record<K, T>' to map category IDs to their specific Metadata.",
                "Implement 'ReturnType<T>' to extract the return type of an existing function.",
                "Use 'Readonly<T>' to prevent modification of an environment configuration object."
            ]
        },
        {
            title: "Type Guards & Narrowing",
            questions: [
                "Implement a 'User-Defined Type Guard' using the 'is' keyword.",
                "Use 'typeof' and 'instanceof' to narrow types within a complex function.",
                "Demonstrate 'Discriminated Unions' using a 'kind' or 'type' literal property.",
                "Handle 'null' and 'undefined' using Optional Chaining and Nullish Coalescing.",
                "Use the 'never' type in a switch-case to ensure exhaustive checking."
            ]
        },
        {
            title: "TS with React & Ecosystem",
            questions: [
                "Define 'Props' and 'State' interfaces for a React functional component.",
                "Type a 'useRef' hook for a HTMLInputElement properly.",
                "Create a custom 'useFetch' hook using Generics for the returned data.",
                "Type a React 'ChangeEvent' for a select dropdown menu.",
                "Implement an 'Async Function' that returns a Promise<T> from a typed API."
            ]
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 8, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h2"} sx={{ fontWeight: 900, color: "#E5C95F", mb: 1 }}>
                    TYPESCRIPT MASTER CHALLENGE
                </Typography>
                <Chip
                    label="PRODEV CONSULT · FINNISH ENGINEERING STANDARDS"
                    sx={{ bgcolor: "rgba(229, 201, 95, 0.1)", color: "#E5C95F", fontWeight: 800, border: "1px solid #E5C95F" }}
                />
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={2}>
                            <ShieldIcon sx={{ fontSize: 40, color: "#E5C95F" }} />
                            <Typography variant="h5" fontWeight={800} color="#E5C95F">Type Architecture Strategy</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.8, mb: 4, lineHeight: 1.8 }}>
                            TypeScript is about more than just "adding types." This master assignment challenges you to build **Scalable Type Systems**, master **Generics**, and implement **Type Guards** to eliminate runtime errors.
                        </Typography>

                        <Grid container spacing={2}>
                            {[
                                "Master Generics & Constraints",
                                "Implement Discriminated Unions",
                                "Advanced Utility Types (Omit, Pick)",
                                "Strict Type Narrowing & Guards",
                                "Enterprise-Scale Interface Merging"
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                        <ArchitectureIcon sx={{ color: "#00ED64", fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{item}</Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>

                {assignmentData.map((topic, index) => (
                    <Grid item xs={12} key={index}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(229, 201, 95, 0.15)", color: "#E5C95F", border: "1px solid #E5C95F" }}>
                                        <TerminalIcon size={22} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={800}>{topic.title}</Typography>
                                </Box>

                                <Stack direction="row" spacing={1.5}>
                                    <Tooltip title={copiedIndex === index ? "Copied!" : "Copy Tasks"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(topic.questions, index)}
                                            sx={{ color: "white", borderColor: "rgba(255,255,255,0.2)" }}
                                        >
                                            {copiedIndex === index ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            startIcon={<CodeIcon />}
                                            sx={{ bgcolor: "#E5C95F", color: "#100720", fontWeight: 700 }}
                                        >
                                            TS Compiler
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                {topic.questions.map((q, i) => (
                                    <QuestionItem key={i}>
                                        <Typography sx={{ color: "#E5C95F", fontWeight: 900, minWidth: "25px" }}>{i + 1}.</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.85 }}>{q}</Typography>
                                    </QuestionItem>
                                ))}
                            </Box>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 6, mb: 10 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={3} justifyContent="center" alignItems="center">
                        <Link to="/typescript-dashboard" style={{ textDecoration: "none" }}>
                            <ActionButton variant="contained">
                                BACK TO DASHBOARD
                            </ActionButton>
                        </Link>
                    </Stack>
                </Grid>
            </Grid>

            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    sx={{ position: "fixed", bottom: 30, right: 30, bgcolor: "#E5C95F" }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
